import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import { Copy, MoreHorizontal, RefreshCcw, Trash2, UserPlus, X } from 'lucide-react';
import { ROLES, ROLE_RANK, type Role } from '@vidmakr/shared';
import { useOrgPlan } from '@/lib/billing';
import { Avatar, Badge, Button, Card, ConfirmModal, Field, Input, Menu, Modal, PageHeader, Select } from '@/components/ui';
import { openContextMenu } from '@/components/ContextMenu';
import { GroupsSection } from '@/components/Groups';
import { useOrg } from '@/lib/hooks';
import { useSession } from '@/lib/session';
import { reducers } from '@/lib/stdb';
import { attempt } from '@/lib/toast';
import { timeAgo } from '@/lib/format';

const ROLE_HELP: Record<Role, string> = {
  owner: 'Full control, billing and deletion',
  admin: 'Manage members, invites and projects',
  editor: 'Edit projects, upload and export',
  viewer: 'Watch, comment and review',
  guest: 'Only the spaces and folders shared with them',
};

export function MembersPage() {
  const { orgId } = useParams();
  const { org, role, members, can } = useOrg(orgId);
  const plan = useOrgPlan(orgId);
  const me = useSession((s) => s.user);
  const [invites] = useTable(tables.orgInvite.where((i) => i.orgId.eq(orgId ?? '')));
  const [presence] = useTable(tables.presence.where((p) => p.orgId.eq(orgId ?? '')));
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const [leaving, setLeaving] = useState(false);
  const online = useMemo(() => new Set(presence.map((p) => p.userId)), [presence]);
  const pending = invites.filter((i) => i.status === 'pending' || i.status === 'expired');
  const sorted = [...members].sort((a, b) => ROLE_RANK[b.role as Role] - ROLE_RANK[a.role as Role] || a.name.localeCompare(b.name));
  if (!org) return null;

  return (
    <>
      <PageHeader
        title="Members"
        subtitle={
          <>
            {members.length} member{members.length === 1 ? '' : 's'} in {org.name}
            {plan?.enabled ? (
              <span data-seat-usage>
                {' · '}
                {plan.editors} of {Number.isFinite(plan.limits.maxEditors) ? plan.limits.maxEditors : '∞'} editor seats
                {Number.isFinite(plan.limits.maxViewers) ? `, ${plan.viewers} of ${plan.limits.maxViewers} viewers` : ''} ·{' '}
                <Link to={`/orgs/${org.id}/billing`} className="text-accent-2 hover:underline">
                  {role === 'owner' ? 'Add seats' : 'Plan'}
                </Link>
              </span>
            ) : null}
          </>
        }
        actions={
          <>
            <Button variant="ghost" onClick={() => setLeaving(true)}>
              Leave organization
            </Button>
            {can('admin') ? (
              <Button variant="primary" onClick={() => setInviting(true)}>
                <UserPlus size={16} /> Invite
              </Button>
            ) : null}
          </>
        }
      />
      <Card>
        {sorted.map((m, i) => {
          const isMe = m.userId === me?.userId;
          const canManage = can('admin') && !isMe && ROLE_RANK[role as Role] >= ROLE_RANK[m.role as Role];
          return (
            <div
              key={m.id}
              className={`flex items-center gap-3 px-4 py-3 ${i ? 'border-t border-line' : ''}`}
              onContextMenu={(e) =>
                openContextMenu(e, [
                  { label: 'Copy email', icon: <Copy size={14} />, onClick: () => void navigator.clipboard.writeText(m.email) },
                  canManage
                    ? {
                        label: 'Change role',
                        submenu: ROLES.filter((r) => role === 'owner' || (r !== 'owner' && r !== 'admin') || r === m.role).map((r) => ({
                          label: r,
                          checked: m.role === r,
                          onClick: () => void attempt(() => reducers().setMemberRole({ memberId: m.id, role: r }), 'Role updated'),
                        })),
                      }
                    : null,
                  canManage ? 'sep' : null,
                  canManage ? { label: 'Remove from organization', icon: <Trash2 size={14} />, danger: true, onClick: () => setRemoving({ id: m.id, name: m.name || m.email }) } : null,
                ])
              }
            >
              <div className="relative">
                <Avatar name={m.name || m.email} url={m.avatarUrl} size={34} />
                {online.has(m.userId) ? <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-panel bg-ok" title="Editing now" /> : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {m.name || m.email} {isMe ? <span className="text-faint">(you)</span> : null}
                </div>
                <div className="truncate text-xs text-faint">
                  {m.email} · joined {timeAgo(m.joinedAt)}
                </div>
              </div>
              {canManage ? (
                <Select
                  value={m.role}
                  onChange={(e) => void attempt(() => reducers().setMemberRole({ memberId: m.id, role: e.target.value }), 'Role updated')}
                >
                  {m.role === 'guest' ? (
                    <option value="guest" disabled>
                      guest
                    </option>
                  ) : null}
                  {ROLES.filter((r) => role === 'owner' || (r !== 'owner' && r !== 'admin') || r === m.role).map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
              ) : (
                <Badge tone={m.role === 'owner' ? 'accent' : 'neutral'}>{m.role}</Badge>
              )}
              {canManage ? (
                <Menu
                  trigger={
                    <Button variant="ghost" size="icon-sm">
                      <MoreHorizontal size={15} />
                    </Button>
                  }
                  items={[{ label: 'Remove from organization', icon: <Trash2 size={14} />, danger: true, onClick: () => setRemoving({ id: m.id, name: m.name || m.email }) }]}
                />
              ) : (
                <div className="w-7" />
              )}
            </div>
          );
        })}
      </Card>
      <GroupsSection orgId={org.id} canManage={can('admin')} />

      {can('admin') && pending.length ? (
        <>
          <h2 className="mb-3 mt-8 text-sm font-semibold text-muted">Pending invitations</h2>
          <Card>
            {pending.map((inv, i) => (
              <div
                key={inv.id}
                className={`flex items-center gap-3 px-4 py-3 ${i ? 'border-t border-line' : ''}`}
                onContextMenu={(e) =>
                  openContextMenu(e, [
                    { label: 'Copy email', icon: <Copy size={14} />, onClick: () => void navigator.clipboard.writeText(inv.email) },
                    { label: 'Resend invitation', icon: <RefreshCcw size={14} />, onClick: () => void attempt(() => reducers().resendInvite({ inviteId: inv.id }), 'Invitation resent') },
                    'sep',
                    { label: 'Revoke invitation', icon: <X size={14} />, danger: true, onClick: () => void attempt(() => reducers().revokeInvite({ inviteId: inv.id })) },
                  ])
                }
              >
                <Avatar name={inv.email} size={30} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm">{inv.email}</div>
                  <div className="text-xs text-faint">
                    Invited by {inv.invitedByName} {timeAgo(inv.createdAt)} · {inv.status === 'expired' ? <span className="text-warn">expired</span> : 'pending'}
                  </div>
                </div>
                <Badge>{inv.role}</Badge>
                <Button variant="ghost" size="icon-sm" title="Resend" onClick={() => void attempt(() => reducers().resendInvite({ inviteId: inv.id }), 'Invitation resent')}>
                  <RefreshCcw size={14} />
                </Button>
                <Button variant="ghost" size="icon-sm" title="Revoke" onClick={() => void attempt(() => reducers().revokeInvite({ inviteId: inv.id }))}>
                  <X size={14} />
                </Button>
              </div>
            ))}
          </Card>
        </>
      ) : null}

      <InviteModal open={inviting} onClose={() => setInviting(false)} orgId={org.id} isOwner={role === 'owner'} />
      <ConfirmModal
        open={!!removing}
        onClose={() => setRemoving(null)}
        title="Remove member"
        danger
        confirmLabel="Remove"
        message={`${removing?.name} will lose access to all projects and assets in ${org.name}.`}
        onConfirm={() => attempt(() => reducers().removeMember({ memberId: removing!.id }), 'Member removed')}
      />
      <ConfirmModal
        open={leaving}
        onClose={() => setLeaving(false)}
        title="Leave organization"
        danger
        confirmLabel="Leave"
        message={`You will lose access to ${org.name}.`}
        onConfirm={async () => {
          if (await attempt(() => reducers().leaveOrg({ orgId: org.id }))) window.location.href = '/';
        }}
      />
    </>
  );
}

function InviteModal({ open, onClose, orgId, isOwner }: { open: boolean; onClose: () => void; orgId: string; isOwner: boolean }) {
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState<Role>('editor');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const list = emails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    let ok = 0;
    for (const email of list) if (await attempt(() => reducers().createInvite({ orgId, email, role }))) ok++;
    setBusy(false);
    if (ok) {
      setEmails('');
      onClose();
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invite people"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={send}>
            Send invitations
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Email addresses" hint="Separate with commas">
          <Input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="ana@company.com, ben@company.com" autoFocus />
        </Field>
        <div>
          <div className="mb-1.5 text-xs font-medium text-muted">Role</div>
          <div className="grid gap-2">
            {ROLES.filter((r) => isOwner || r !== 'owner').map((r) => (
              <label
                key={r}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 ${role === r ? 'border-accent bg-accent/10' : 'border-line hover:bg-panel-2'}`}
              >
                <input type="radio" checked={role === r} onChange={() => setRole(r)} className="accent-[var(--color-accent)]" />
                <div>
                  <div className="text-sm font-medium capitalize">{r}</div>
                  <div className="text-xs text-muted">{ROLE_HELP[r]}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
